import NliraCustomBrand from "@/shared/lib/client-sdk/models/nlira-custom-brand";

/**
 * NLIRA Custom Branding Store Module
 *
 * Shared store module for NextLevel IRA custom branding functionality
 * Handles logo fetching and caching for custom brand logos
 * Can be imported into any store that needs branding functionality
 *
 * Usage:
 * import nliraCustomBranding from "@/shared/store/modules/nlira-custom-branding";
 *
 * modules: {
 *   nliraCustomBranding
 * }
 *
 * Access Options:
 * 1. Direct state: $store.state.nliraCustomBranding?.effectiveNliraCustomBrandLogo
 * 2. Via mixin: NliraCustomBrandingMixin (recommended for components)
 */

// ========================
// PRIVATE HELPER FUNCTIONS
// ========================
// These functions are only used within this store module

const CACHE_KEY = "effectiveNliraCustomBrand";

/**
 * Retrieve the cached logo from localStorage if it matches the provided brand's id and lastUpdated.
 * @param {Object} brand - Brand object containing id and lastUpdated properties.
 * @returns {string|null} The cached logo string if available and up-to-date, otherwise null.
 */
function getCachedLogo(brand) {
  try {
    const cache = localStorage.getItem(CACHE_KEY);
    if (cache) {
      const parsed = JSON.parse(cache);
      // Return the logo if it is the latest version of the brand
      if (
        parsed.id === brand.id &&
        parsed.lastUpdated === brand.lastUpdated &&
        parsed.logo
      ) {
        return parsed.logo;
      }
    }
  } catch (error) {
    console.warn("Invalid NLIRA brand cache data:", error);
  }
  return null;
}

/**
 * Check localStorage cache for logo
 * @returns {object|null} Cached brand or null
 */
function getCachedBrand() {
  try {
    const cache = localStorage.getItem(CACHE_KEY);
    if (cache) {
      const parsed = JSON.parse(cache);
      return parsed;
    }
  } catch (error) {
    console.warn("Invalid NLIRA brand cache data:", error);
  }
  return null;
}

/**
 * Save brand to localStorage cache
 * @param {Object} brand - Brand info
 */
function setCachedBrand(brand) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(brand));
  } catch (error) {
    console.warn("Failed to cache NLIRA brand:", error);
  }
}

/**
 * Clear cached logo data from localStorage
 */
function clearCache() {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.warn("Failed to clear NLIRA brand cache:", error);
  }
}

// ========================
// STORE MODULE
// ========================
export default {
  namespaced: true,

  state: {
    // Stores the fetched custom brand info (id, displayName, lastUpdated, etc) and logo (base64 string or null)
    effectiveNliraCustomBrand: null,
    isLoadingEffectiveNliraCustomBrand: false,
  },

  mutations: {
    /**
     * Sets the custom brand logo in state
     * @param {Object} state - Module state
     * @param {string|null} logo - Base64 logo string or null to clear
     */
    setEffectiveNliraCustomBrand(state, brand) {
      state.effectiveNliraCustomBrand = brand;
    },
    /**
     * Sets the loading state for the effective custom brand
     * @param {Object} state - Module state
     * @param {boolean} loading - Whether the effective custom brand is loading
     */
    setIsLoadingEffectiveNliraCustomBrand(state, loading) {
      state.isLoadingEffectiveNliraCustomBrand = loading;
    },
  },

  actions: {
    /**
     * Determines and sets the effective custom brand for the application.
     *
     * The function attempts to resolve the brand using the following priority:
     *   1. The provided `brand` argument, if supplied.
     *   2. The authenticated user's `effectiveNliraCustomBrand` from the Vuex root state.
     *   3. The most recently cached brand from localStorage.
     * If no brand is found from any of these sources, the effective custom brand is cleared from state and cache.
     *
     * Also fetches the custom brand logo from the API if it is not already present in the resolved brand,
     * and caches the complete brand object (including the logo) in localStorage for future use.
     *
     * @param {Object} context - Vuex action context (contains commit, dispatch, rootState, etc)
     * @param {Object} payload - Optional payload object containing:
     * @param {Object} payload.brand - Optional. Brand object to use as the effective brand.
     * @param {string} payload.partnerSourceUuid - Optional. Partner source UUID to use to fetch the custom brand.
     * @param {string} payload.prospectUuid - Optional. Prospect UUID to use to fetch the custom brand.
     * @param {boolean} payload.saveToCache - Optional. Whether to save the resolved brand to localStorage (default: true)
     */
    async fetchEffectiveCustomBrand(
      { commit, dispatch, rootState },
      payload = {}
    ) {
      commit("setIsLoadingEffectiveNliraCustomBrand", true);
      const {
        brand = null,
        partnerSourceUuid = null,
        prospectUuid = null,
        saveToCache = true,
      } = payload;

      // Helper to resolve the brand from various sources, in the following priority order:
      // 1. brand, 2. partnerSourceUuid, 3. generalUuid, 4. authenticatedUser, 5. localStorage cache.
      const resolveBrand = async () => {
        if (brand) {
          return brand;
        }
        if (partnerSourceUuid) {
          return await NliraCustomBrand.getBrandByPartnerSourceUuid(
            partnerSourceUuid
          );
        }
        if (prospectUuid) {
          try {
            return await NliraCustomBrand.getBrandByProspectUuid(prospectUuid);
          } catch (error) {
            console.warn(
              "Failed to fetch NLIRA custom brand by prospect uuid:",
              error
            );
          }
        }
        if (rootState?.authenticatedUser) {
          // User is authenticated
          if (rootState.authenticatedUser.effectiveNliraCustomBrand) {
            return rootState.authenticatedUser.effectiveNliraCustomBrand;
          }
          // Authenticated but no brand, return null (do not fallback to cache)
          return null;
        }
        // Fallback to cached brand
        return getCachedBrand();
      };

      const effectiveBrand = await resolveBrand();
      if (!effectiveBrand) {
        // If no brand is found from any source, clear the effective custom brand from state and cache, then exit
        dispatch("clearEffectiveCustomBrand");
      } else {
        // If the brand object does not include a logo, attempt to retrieve the logo from the cached brand (if available and up-to-date)
        effectiveBrand.logo =
          effectiveBrand.logo ?? getCachedLogo(effectiveBrand);
        if (!effectiveBrand.logo) {
          // If the logo is not in the brand, fetch the logo from the API using the custom brand ID
          try {
            effectiveBrand.logo = await NliraCustomBrand.getLogo(
              effectiveBrand.id
            );
          } catch (error) {
            console.warn("Failed to fetch NLIRA custom brand logo:", error);
          }
        }
        // At this point, the brand object is guaranteed to include a logo (either from cache or freshly fetched).
        // Update the Vuex state with the resolved effective custom brand.
        commit("setEffectiveNliraCustomBrand", effectiveBrand);

        // Optionally cache the complete brand object (including the logo) in localStorage for future retrieval.
        if (saveToCache) {
          setCachedBrand(effectiveBrand);
        }
      }
      commit("setIsLoadingEffectiveNliraCustomBrand", false);
    },

    /**
     * Clear the custom brand logo from state and cache
     * @param {Object} context - Vuex action context
     */
    clearEffectiveCustomBrand({ commit }) {
      commit("setEffectiveNliraCustomBrand", null);
      clearCache(); // Use private helper
    },
  },
};
